import type { ShadowShape } from "../api/types";

/**
 * Форма счёта для экрана: чтение гистограммы и приговор о хрупкости.
 *
 * # Что этот блок обязан сказать
 *
 * Одно: держится ли счёт на кромке или на горстке сделок. Вопрос не праздный —
 * на живых данных 2026-08-28 он разводит две подсистемы, которые по колонке
 * «счёт» выглядят одинаково успешными:
 *
 *   - расхождение модели и стакана: +$1542 на 717 сделках, но медиана −$1.16 и
 *     без десяти лучших сделок −$3.88. Плюс собран в 1.4 % строк;
 *   - почти решённое: +$110 на 54 входах, медиана +$1.77, без трёх лучших
 *     остаётся +$59.74. Плюс размазан по сделкам.
 *
 * Первое — это лотерейный билет, купленный 717 раз; второе — кромка. Ставить
 * деньги под них надо по-разному, а колонка «счёт» у них одного знака.
 */

/** Число из строки контракта. null означает прочерк, а не ноль. */
function num(v: string | null | undefined): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Подписи корзин — ГРАНИЦЫ, а не середины.
 *
 * Ровно те, что зашиты в storage.PnLShape, и в том же порядке. Разъедься они —
 * гистограмма молча подписала бы чужие столбики, что хуже отсутствия подписей.
 */
export const SHAPE_EDGES = [-25, -10, -5, -2, 0, 2, 5, 10, 25] as const;

/**
 * Подпись одной корзины для подсказки: «−5…−2», «≥ +25».
 *
 * Индекс вне диапазона отдаёт пустую строку, а не падает: границы задаёт сервер,
 * и если он однажды пришлёт одиннадцать корзин, подсказка обязана недосказать, а
 * не уронить панель.
 */
export function binLabel(i: number): string {
  const fmt = (v: number) => (v > 0 ? `+${v}` : `${v}`).replace("-", "−");
  const lo = SHAPE_EDGES[i - 1];
  const hi = SHAPE_EDGES[i];
  if (i === 0) return `< ${fmt(SHAPE_EDGES[0])}`;
  if (i === SHAPE_EDGES.length) {
    return `≥ ${fmt(SHAPE_EDGES[SHAPE_EDGES.length - 1] as number)}`;
  }
  if (lo === undefined || hi === undefined) return "";
  return `${fmt(lo)}…${fmt(hi)}`;
}

/**
 * Индекс первой корзины с прибылью.
 *
 * Нужен для окраски: убыточные корзины красятся в цвет продажи, прибыльные — в
 * цвет покупки. Граница проходит по нулю, а не по середине гистограммы: корзин
 * слева и справа разное число, и «половина столбиков» означала бы не то.
 */
export const SHAPE_FIRST_PROFIT_BIN = SHAPE_EDGES.indexOf(0) + 1;

/** Деньги со знаком. Прочерк — считать было не из чего. */
export function money(v: string | null | undefined): string {
  const n = num(v);
  if (n === null) return "—";
  const sign = n > 0 ? "+" : n < 0 ? "−" : "";
  return `${sign}$${Math.abs(n).toFixed(2)}`;
}

/**
 * Цвет суммы. Красится ТОЛЬКО минус — та же асимметрия, что у ставки и
 * приговора форме: зелёное на семи панелях сразу перестаёт замечаться, а
 * отрицательные деньги обязаны бросаться в глаза везде, где показаны. Прочерк
 * и измеренный ноль не красятся: ни тот ни другой не минус.
 */
export function moneyTone(v: string | null | undefined): string | undefined {
  const n = num(v);
  if (n === null || n >= 0) return undefined;
  return "var(--color-bad)";
}

/**
 * Приговор форме одной строкой.
 *
 * Порядок проверок — от самого дорогого заблуждения к самому мелкому.
 *
 * ПЕРВОЙ идёт хрупкость, а не знак счёта: «плюс, который держится на трёх
 * сделках» — единственный вывод, ради которого форму и считают, и молчать о нём
 * нельзя даже когда сумма выглядит убедительно.
 *
 * Медиана проверяется ВТОРОЙ и отдельно от хрупкости: счёт может пережить
 * снятие трёх лучших сделок и всё равно стоять на убыточной типичной сделке —
 * так выглядит длинный хвост при массе слева.
 */
export function fragility(s: ShadowShape | undefined): string {
  if (!s || s.n === 0) return "сделок не было";
  const sum = num(s.sum) ?? 0;
  const cut = num(s.without_top3) ?? 0;
  const median = num(s.median) ?? 0;
  if (s.n <= 3) return `${s.n} сделки — судить не о чем`;
  if (sum > 0 && cut <= 0) {
    return `ХРУПКИЙ ПЛЮС: без трёх лучших сделок счёт ${money(s.without_top3)}`;
  }
  if (sum > 0 && median <= 0) {
    return `плюс есть, но типичная сделка убыточна (медиана ${money(s.median)})`;
  }
  if (sum <= 0 && median > 0) {
    return `типичная сделка плюсовая, счёт съеден крупными потерями`;
  }
  if (sum > 0) return `ровный плюс: медиана ${money(s.median)}, без трёх лучших ${money(s.without_top3)}`;
  return `минус: медиана ${money(s.median)}`;
}

/**
 * Цвет приговора. Красится ТОЛЬКО опасное, а хорошее остаётся серым.
 *
 * Сознательная асимметрия. Зелёная строка на пяти панелях сразу читается как
 * «всё в порядке» и перестаёт замечаться; красная нужна там, где вывод
 * противоположен тому, что показывает колонка «счёт», — то есть ровно там, где
 * без формы ошиблись бы.
 */
export function fragilityTone(s: ShadowShape | undefined): string | undefined {
  if (!s || s.n <= 3) return undefined;
  const sum = num(s.sum) ?? 0;
  const cut = num(s.without_top3) ?? 0;
  const median = num(s.median) ?? 0;
  if (sum > 0 && (cut <= 0 || median <= 0)) return "var(--color-warn)";
  return undefined;
}

/**
 * # Удельный счёт: сколько подсистема приносит за час и за сутки работы
 *
 * ## Зачем это вообще
 *
 * Затем, что суммы теневых журналов НЕСРАВНИМЫ по построению. Подсистемы
 * заводились в разные дни, и на 2026-08-29 почти решённое живёт 33 часа,
 * консенсус семьи — часы, копирование — недели. «+$189 против +$12» в такой
 * расстановке сравнивает не кромки, а ВОЗРАСТ, и колонка «счёт» этого не
 * показывает никак — ровно как сумма без знаменателя не показывает, из скольких
 * сделок она сложена.
 *
 * Час и сутки приводят подсистемы к одной мере. Это ответ на вопрос «какую из
 * них стоит кормить деньгами», и он ОТЛИЧАЕТСЯ от вопроса, на который отвечает
 * доходность к обороту. Доходность мерит КПД вложенного доллара, удельный счёт —
 * пропускную способность: подсистема с кромкой в 20 % на одной сделке в неделю
 * проигрывает подсистеме с 2 % на сотне сделок в день, и наоборот при нехватке
 * денег. Оба числа нужны, и ни одно не заменяет другое.
 *
 * ## Чем делим
 *
 * Сроком от НАЧАЛА ОКНА сводки до последней строки группы — и по ВСЕМ строкам,
 * а не по сделкам (см. `ShadowShape.observed`). Закрывается срок последней
 * строкой, а не «сейчас»: остановленная подсистема обязана сохранить
 * показанную ставку, а не размываться простоем.
 *
 * Открывается срок началом окна, а не первой строкой, и это выстрадано. У
 * строки-группы, живущей всплесками — семьи, клетки сетки смещённого края, —
 * срок «первая строка → последняя» терял простои до всплеска: семья с часом
 * жизни в журнале превращала −$50 на двух заявках в «−$1031/сут», клетка
 * сетки — три сделки за 1.3 часа в «−$1465/сут», и колонка ставок читалась
 * как минус портфеля, которого нет. Начало окна возвращает простои в
 * знаменатель. Новый срок не бывает КОРОЧЕ старого — первая строка всегда
 * внутри окна, — то есть от правки ставка могла только понизиться: ошибка
 * строго в ту сторону, в которую ошибаться правильно.
 *
 * ## Чего эта ставка НЕ говорит, и это надо знать до того, как ей поверить
 *
 * Срок считается по календарю и включает ПРОСТОИ: выключенная на сутки
 * подсистема получит те же сутки в знаменатель. Значит ставка — это НИЖНЯЯ
 * граница того, что подсистема даёт, пока действительно работает. Ошибаться в
 * эту сторону правильно: завышенная ставка зовёт деньги, заниженная — нет.
 * Насколько плотным было наблюдение, видно по `observed` рядом.
 */

/**
 * Срок группы в часах: от начала окна сводки до последней строки.
 *
 * `hours` — окно сводки, то же число, что ушло в запрос формы. Сверху срок
 * обрезается окном (часы вперёд у рассинхронных часов — не наблюдение), а
 * последняя строка старше окна даёт null: это несвежий кеш, и ставка по нему
 * была бы утверждением о протухших данных.
 */
export function spanHours(s: ShadowShape | undefined, hours: number): number | null {
  if (!s || !s.last_seen) return null;
  const to = Date.parse(s.last_seen);
  if (!Number.isFinite(to)) return null;
  const h = (to - (Date.now() - hours * 3_600_000)) / 3_600_000;
  if (h < 0) return null;
  return Math.min(h, hours);
}

/**
 * Ниже этого срока ставки НЕ БЫВАЕТ.
 *
 * Час — не круглое число ради красоты, а сама единица, в которой ставка
 * объявляется: делить на срок короче единицы значит не измерять, а
 * экстраполировать. Со сроком от начала окна сюда попадает один случай —
 * группа, вся жизнь которой уместилась в первый час окна и вот-вот выедет из
 * него целиком.
 */
const MIN_SPAN_HOURS = 1;

/** Счёт за час работы. null — срок короче часа либо считать не из чего. */
export function perHour(s: ShadowShape | undefined, hours: number): number | null {
  const h = spanHours(s, hours);
  if (h === null || h < MIN_SPAN_HOURS) return null;
  const sum = num(s?.sum);
  if (sum === null) return null;
  return sum / h;
}

/**
 * Счёт за сутки работы.
 *
 * Ровно `perHour × 24`, а не отдельный замер, и отдельной функцией только ради
 * читаемости вызова. Собственного порога у суток НЕТ намеренно: срок в 30 часов
 * — это меньше двух суток, и требуй мы полных суток наблюдения, самая свежая
 * подсистема осталась бы без единственного числа, которым её можно сравнить с
 * соседями. Насколько это экстраполяция, говорит срок, который стои́т рядом.
 */
export function perDay(s: ShadowShape | undefined, hours: number): number | null {
  const h = perHour(s, hours);
  return h === null ? null : h * 24;
}

/** Деньги в единицу времени: «+$5.63/ч». Прочерк — ставки нет. */
export function rate(v: number | null, unit: string): string {
  if (v === null) return "—";
  const sign = v > 0 ? "+" : v < 0 ? "−" : "";
  return `${sign}$${Math.abs(v).toFixed(2)}/${unit}`;
}

/** Срок словами: часы до суток, дальше сутки. */
export function spanWord(s: ShadowShape | undefined, hours: number): string {
  const h = spanHours(s, hours);
  if (h === null) return "срок неизвестен";
  if (h < 48) return `${h.toFixed(1)} ч`;
  return `${(h / 24).toFixed(1)} сут`;
}

/**
 * Удельный счёт одной фразой — вместе со СРОКОМ и ПЛОТНОСТЬЮ наблюдения.
 *
 * Срок здесь обязателен по тому же правилу, по которому знаменатель обязателен
 * рядом с суммой: «+$0.88/ч» на 34 часах и «+$0.88/ч» на месяце — разные
 * утверждения о деньгах, а частное у них одно. Плотность (`observed`) отделяет
 * работавшую непрерывно подсистему от работавшей урывками: срок у них
 * одинаковый, а вес у ставки разный.
 */
export function rateWord(s: ShadowShape | undefined, hours: number): string {
  if (!s) return "наблюдения не было";
  const span = spanHours(s, hours);
  if (span === null) {
    return `ставки нет: последняя строка старше окна (${s.observed} строк)`;
  }
  if (span < MIN_SPAN_HOURS) {
    return `хвост окна ${spanWord(s, hours)} (${s.observed} строк) — короче часа, ставки нет`;
  }
  return (
    `${rate(perDay(s, hours), "сут")} = ${rate(perHour(s, hours), "ч")} ` +
    `за ${spanWord(s, hours)} в окне (${s.observed} строк)`
  );
}

/**
 * Цвет удельной ставки. Красится ТОЛЬКО минус.
 *
 * Та же асимметрия, что у приговора форме: зелёное на семи панелях сразу
 * перестаёт замечаться, а вот отрицательная ставка — это подсистема, которая
 * стоит денег каждый час работы, и молчать об этом нельзя.
 */
export function rateTone(s: ShadowShape | undefined, hours: number): string | undefined {
  const h = perHour(s, hours);
  if (h === null || h >= 0) return undefined;
  return "var(--color-bad)";
}

/**
 * Форма по ключу строки.
 *
 * undefined означает «такой группы не было вовсе» и отличается от формы с
 * `n === 0` — «группу наблюдали, сделок в ней не случилось». Различение не
 * педантизм: вторая уже имеет срок наблюдения, то есть про неё известно, сколько
 * подсистема простояла впустую, а про первую не известно ничего.
 */
export function shapeOf(
  shapes: ShadowShape[] | undefined,
  key: string,
): ShadowShape | undefined {
  return shapes?.find((s) => s.key === key);
}
